import { ApiError, cn, useApiClient } from '@servicetim/ui/lib';
import { useQuery } from '@tanstack/react-query';

type HealthState = 'checking' | 'ok' | 'down';

const STYLES: Record<HealthState, { dot: string; label: string }> = {
  checking: { dot: 'bg-fg-subtle animate-pulse', label: 'API …' },
  ok: { dot: 'bg-emerald-500', label: 'API ok' },
  down: { dot: 'bg-red-500', label: 'API down' },
};

/**
 * Calls `GET {apiBaseUrl}/health` so a fresh checkout proves the API wiring (Vite proxy locally,
 * CloudFront `/api/*` behavior when deployed). Click to re-check.
 */
export function HealthBadge() {
  const api = useApiClient();
  const health = useQuery({
    queryKey: ['health', api.baseUrl],
    queryFn: ({ signal }) => api.get<unknown>('/health', { signal }),
    refetchInterval: 60_000,
    retry: 1,
  });

  const state = toState(health.isPending, health.isError);
  const { dot, label } = STYLES[state];
  const title = health.isError ? describeError(health.error) : `${api.baseUrl}/health`;

  return (
    <button
      type="button"
      onClick={() => void health.refetch()}
      title={title}
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-border bg-surface-raised px-2.5 py-1 font-medium text-fg-muted text-xs',
        'hover:bg-surface-sunken',
        health.isFetching && !health.isPending && 'opacity-70',
      )}
    >
      <span className={cn('size-2 rounded-full', dot)} aria-hidden="true" />
      {label}
    </button>
  );
}

function toState(isPending: boolean, isError: boolean): HealthState {
  if (isPending) return 'checking';
  return isError ? 'down' : 'ok';
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.isNetworkError ? error.message : `HTTP ${error.status}: ${error.message}`;
  }
  return error instanceof Error ? error.message : 'Unknown error';
}
